import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useProject } from '@/hooks/useProjects';
import { useEnvironments, useDeleteEnvironment } from '@/hooks/useEnvironments';
import { useSecrets } from '@/hooks/useSecrets';
import { useOrganization } from '@/hooks/useOrganization';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { SearchBar } from '@/components/ui/SearchBar';
import { EmptyState } from '@/components/ui/EmptyState';
import { Plus, Edit, Lock, GlobeIcon, UnlockIcon, SettingsIcon, MoreHorizontal, HomeIcon, BuildingIcon } from 'lucide-react';
import { EnvironmentDrawer } from '@/components/environments/EnvironmentDrawer';
import { ResourcePageHeader } from '@/components/layout/ResourcePageHeader';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import type { ApiEnvironment } from '@envase/api/client';

export const Route = createFileRoute('/_authenticated/org/$orgId/projects/$id/environments')({
  component: ProjectEnvironmentsPage,
});

function ProjectEnvironmentsPage() {
  const { id: projectId, orgId } = Route.useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const { organization } = useOrganization(orgId);
  const { project } = useProject(projectId, organization?.id);
  const environmentsQuery = useEnvironments(projectId);
  const secretsQuery = useSecrets(projectId);
  const deleteEnvironment = useDeleteEnvironment(projectId);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'protected' | 'open'>('all');
  const [secretsFilter, setSecretsFilter] = useState<'all' | 'with' | 'without'>('all');
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [editingEnvironment, setEditingEnvironment] = useState<ApiEnvironment | undefined>(undefined);

  const environments = environmentsQuery.data?.environments || [];
  const secrets = secretsQuery.data?.secrets || [];

  // Filter environments
  const filteredEnvironments = environments.filter((env) => {
    const matchesQuery = searchQuery
      ? (env.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         env.slug?.toLowerCase().includes(searchQuery.toLowerCase()))
      : true;

    const matchesStatus =
      statusFilter === 'all'
        ? true
        : statusFilter === 'protected'
        ? Boolean(env.protected)
        : !Boolean(env.protected);

    const envSecretCount = secrets.filter((s) => s.environmentId === env.id).length;
    const matchesSecrets =
      secretsFilter === 'all'
        ? true
        : secretsFilter === 'with'
        ? envSecretCount > 0
        : envSecretCount === 0;

    return matchesQuery && matchesStatus && matchesSecrets;
  });

  const handleDelete = async (environmentId: string, environmentName: string) => {
    if (!confirm(`Are you sure you want to delete ${environmentName}?`)) {
      return;
    }

    try {
      console.log('Deleting environment:', environmentId);
      await deleteEnvironment.mutateAsync({ environmentId });
      toast.success('Environment deleted successfully');
      environmentsQuery.refetch();
    } catch (error) {
      console.error('Delete environment error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete environment');
    }
  };

  const handleEdit = (environment: ApiEnvironment) => {
    console.log('Editing environment:', environment);
    setEditingEnvironment(environment);
    setShowCreateDrawer(true);
  };

  const handleCloseDrawer = () => {
    setShowCreateDrawer(false);
    setEditingEnvironment(undefined);
  };

  const breadcrumbs = [
    {
      name: 'Dashboard',
      to: '/dashboard',
      icon: HomeIcon,
    },
    {
      name: organization?.name || 'Organization',
      to: `/org/${organization?.slug || orgId}`,
      icon: BuildingIcon,
    },
    {
      name: project?.name || 'Project',
      to: `/org/${orgId}/projects/${projectId}`,
    },
    {
      name: t('environments.title'),
      to: `/org/${orgId}/projects/${projectId}/environments`,
    },
  ];

  return (
    <div className="space-y-8">
      <ResourcePageHeader
        breadcrumbs={breadcrumbs}
        title={`${project?.name} - ${t('environments.title')}`}
        subtitle={t('environments.description')}
        action={
          <Button onClick={() => setShowCreateDrawer(true)} data-testid="create-environment-button">
            <Plus className="w-4 h-4 mr-2" />
            {t('environments.createNew')}
          </Button>
        }
        backTo={`/org/${orgId}/projects/${projectId}`}
        variant="project"
      />

      <Card>
        <CardContent className="pt-6 space-y-4">
          <SearchBar
            placeholder={t('environments.searchPlaceholder')}
            value={searchQuery}
            onChange={setSearchQuery}
            data-testid="search-environments-input"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="environment-filters">
            <div className="space-y-2">
              <Label>{t('environments.filters.status')}</Label>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'all' | 'protected' | 'open')}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('environments.filters.statusAll')}</SelectItem>
                  <SelectItem value="protected">{t('environments.filters.statusProtected')}</SelectItem>
                  <SelectItem value="open">{t('environments.filters.statusOpen')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('environments.filters.secrets')}</Label>
              <Select value={secretsFilter} onValueChange={(v) => setSecretsFilter(v as 'all' | 'with' | 'without')}>
                <SelectTrigger data-testid="filter-secrets">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('environments.filters.secretsAll')}</SelectItem>
                  <SelectItem value="with">{t('environments.filters.secretsWith')}</SelectItem>
                  <SelectItem value="without">{t('environments.filters.secretsWithout')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm text-gray-500">
            <button
              type="button"
              className="underline"
              onClick={() => { setStatusFilter('all'); setSecretsFilter('all'); setSearchQuery(''); }}
              data-testid="filters-clear"
            >
              {t('environments.filters.clear')}
            </button>
            <span data-testid="filters-results">
              {t('environments.filters.results', { count: filteredEnvironments.length })}
            </span>
          </div>
        </CardContent>
      </Card>

      <div className="border rounded-lg">
        {filteredEnvironments.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50%]">{t('environments.table.environment')}</TableHead>
                <TableHead className="w-[20%]">{t('environments.table.status')}</TableHead>
                <TableHead className="w-[15%] text-right">{t('environments.table.secrets')}</TableHead>
                <TableHead className="w-[15%] text-right">{t('environments.table.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEnvironments.map((environment) => {
                const envSecretCount = secrets.filter((s) => s.environmentId === environment.id).length;
                return (
                  <TableRow key={environment.id} data-testid={`environment-row-${environment.slug || environment.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="p-1.5 bg-blue-100 rounded">
                          <GlobeIcon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="font-medium truncate" title={environment.name}>{environment.name}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {environment.protected === true ? (
                        <Badge variant="destructive" className="inline-flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          {t('environments.badge.protected')}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="inline-flex items-center gap-1">
                          <UnlockIcon className="w-3 h-3" />
                          {t('environments.badge.open')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-right">{envSecretCount}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`environment-actions-${environment.id}`}
                            title={t('common.actions')}
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(environment)} data-testid={`environment-action-edit-${environment.id}`}>
                            <Edit className="w-4 h-4 mr-2" /> {t('environments.edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`environment-action-settings-${environment.id}`}>
                            <SettingsIcon className="w-4 h-4 mr-2" /> {t('environments.settings')}
                          </DropdownMenuItem>
                          {environment.protected !== true && (
                            <DropdownMenuItem
                              onClick={() => handleDelete(environment.id, environment.name)}
                              className="text-red-600 focus:text-red-700"
                              data-testid={`environment-action-delete-${environment.id}`}
                            >
                              {t('environments.delete')}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            title={t('environments.empty.title')}
            description={searchQuery ? t('environments.empty.searchHint') : t('environments.empty.getStarted')}
            action={!searchQuery ? {
              label: t('environments.createNew'),
              onClick: () => setShowCreateDrawer(true),
            } : undefined}
          />
        )}
      </div>

      {/* Create/Edit Environment Drawer */}
      <EnvironmentDrawer
        open={showCreateDrawer}
        onOpenChange={handleCloseDrawer}
        projectId={projectId}
        environment={editingEnvironment}
        onSuccess={() => {
          environmentsQuery.refetch();
          handleCloseDrawer();
        }}
      />
    </div>
  );
}
